// Shared recipe-details prompt builder for initial generation and replacements.
import { getCurrentLanguage } from '../services/i18n';
import { getCompactNamingRule } from './ingredientNamingRules';
import { getShelfLifeCatalog } from './ingredientQualityRules';
import { getCookingTimeLimit, getEffectiveCookingTimeLimit } from './cookingTimeLimits';

const LANG_NAMES = {
  es: 'Spanish (Español)',
  ru: 'Russian (Русский)',
  ca: 'Catalan (Català)',
  fr: 'French (Français)',
  ro: 'Romanian (Română)',
  it: 'Italian (Italiano)',
  uk: 'Ukrainian (Українська)'
};

const COMPLEXITY_GUIDE = {
  simple: {
    steps: '3-4',
    technique: 'Easy, low-attention techniques only: assembly, toasting, boiling, sheet-pan/oven roasting, or one-pot cooking. Minimal prep; no elaborate sauces or plating.',
    equipment: 'Basic tools only: knife, cutting board, one pan or pot, baking sheet, toaster if needed.'
  },
  standard: {
    steps: '3-5',
    technique: 'Easy, mostly-passive techniques: roasting, baking, sheet-pan, or one-pot simmering with minimal hands-on attention. Prefer passive oven/simmer time over active stovetop work; no fussy multi-step components.',
    equipment: 'Standard home kitchen tools: pots, pans, oven, basic utensils. No special equipment.'
  },
  advanced: {
    steps: '3-5',
    technique: 'Still easy and hands-off — use the extra time for deeper flavour via slow oven-braising, roasting, or one-pot simmering. The extra time is UNATTENDED: keep active work minimal; never chef-tier technique or multi-pan juggling.',
    equipment: 'Standard home tools only: pots, pans, oven, blender, basic utensils. No professional equipment.'
  }
};

const LEGACY_OVEN_PREFERENCE_RULE =
  '- Prefer oven roasting/baking for fish, chicken, and vegetables when equally suitable, unless the title implies pan-frying/stir-frying.';

const LOW_STRESS_COOKING_RULE =
  `- Low-stress deliciousness is the BASELINE for EVERY recipe and every complexity tier: great flavor with minimal active attention. Default to ONE mostly-passive method (oven roast/bake, sheet-pan, or one-pot) and do NOT pre-fry or pre-boil components and then bake — that doubles the work.
- Use oven roasting/baking/sheet-pan for fish, chicken, vegetables, potatoes, and similar ingredients. Only use pan-frying/stir-frying when the title explicitly implies it (stir-fry, pan-seared, fried, skillet); otherwise keep it in the oven/one-pot.
- Prefer simple sauces/sides: pan drippings, lemon + olive oil, herb-oil sauce, jarred condiments, quick salad, bread, or fruit. Avoid multi-step reductions, breakable emulsions, and elaborate sauce builds at every tier — a higher tier means more passive cooking time, not more active steps.`;

const STEP_TITLE_EXAMPLES = {
  en: {
    prep: 'Prepare the ingredients (5 min)',
    cook: 'Roast until golden (20 min)',
    finish: 'Plate and finish (3 min)',
    thaw: 'Thaw the frozen seafood (30 min)'
  },
  es: {
    prep: 'Prepara los ingredientes (5 min)',
    cook: 'Hornea hasta dorar (20 min)',
    finish: 'Sirve y termina (3 min)',
    thaw: 'Descongela el marisco congelado (30 min)'
  },
  ru: {
    prep: 'Подготовьте ингредиенты (5 мин)',
    cook: 'Запеките до румяности (20 мин)',
    finish: 'Выложите и завершите (3 мин)',
    thaw: 'Разморозьте замороженные морепродукты (30 мин)'
  },
  ca: {
    prep: 'Prepara els ingredients (5 min)',
    cook: 'Enforna fins que dauri (20 min)',
    finish: 'Emplata i acaba (3 min)',
    thaw: 'Descongela el marisc congelat (30 min)'
  },
  fr: {
    prep: 'Preparez les ingredients (5 min)',
    cook: 'Rotissez jusqu a dorer (20 min)',
    finish: 'Dressez et terminez (3 min)',
    thaw: 'Decongelez les fruits de mer (30 min)'
  },
  ro: {
    prep: 'Pregateste ingredientele (5 min)',
    cook: 'Coace pana se rumeneste (20 min)',
    finish: 'Monteaza si finalizeaza (3 min)',
    thaw: 'Dezgheata fructele de mare (30 min)'
  },
  it: {
    prep: 'Prepara gli ingredienti (5 min)',
    cook: 'Cuoci fino a doratura (20 min)',
    finish: 'Impiatta e rifinisci (3 min)',
    thaw: 'Scongela i frutti di mare (30 min)'
  },
  uk: {
    prep: 'Підготуйте інгредієнти (5 хв)',
    cook: 'Запечіть до румяності (20 хв)',
    finish: 'Викладіть і завершіть (3 хв)',
    thaw: 'Розморозьте морепродукти (30 хв)'
  }
};

const normalizeIngredients = (recipe) => {
  if (Array.isArray(recipe?.ingredients)) {
    return recipe.ingredients;
  }

  if (recipe?.ingredients && typeof recipe.ingredients === 'object') {
    return Object.entries(recipe.ingredients).map(([product, qty]) => ({ product, qty }));
  }

  return [];
};

const formatIngredientLine = (ingredient) => {
  const name = ingredient.product || ingredient.name || '';
  const qty = ingredient.qty || ingredient.quantity || '';
  return `${qty} ${name}`.trim();
};

// Supermarket forms that change how an ingredient is handled in the kitchen.
// "fresh"/"other" need no tag; the rest are surfaced so the model doesn't treat
// e.g. canned crushed tomatoes as a fresh tomato to slice. The meal-plan stage
// already sets `form` per ingredient (see promptGenerators.js), but it is dropped
// during grocery consolidation — so we read it from the recipe ingredient here.
const FORM_TAGGED = new Set(['frozen', 'canned', 'jarred', 'dried', 'cured', 'prepared', 'dairy']);

const getFormTag = (ingredient) => {
  const form = String(ingredient?.form || '').trim().toLowerCase();
  return FORM_TAGGED.has(form) ? ` [${form}]` : '';
};

const getLocalizedName = (item) => {
  const currentLang = getCurrentLanguage();
  let langMap = item?.lang;

  if (typeof langMap === 'string') {
    try { langMap = JSON.parse(langMap); } catch { langMap = null; }
  }

  if (langMap && typeof langMap === 'object') {
    const exact = langMap[currentLang];
    if (exact) return exact;

    const base = currentLang?.split('-')[0];
    if (base && langMap[base]) return langMap[base];
  }

  return item?.displayName || item?.productName || null;
};

export const buildIngredientContext = (recipe, matchedProducts) => {
  const ingredients = normalizeIngredients(recipe);

  if (ingredients.length === 0) {
    return { ingredientsWithContext: 'No ingredients specified', substitutions: [], matchedNames: '' };
  }

  if (!matchedProducts || matchedProducts.length === 0) {
    const ingredientsText = ingredients.map(ing => `${formatIngredientLine(ing)}${getFormTag(ing)}`).join(', ');
    const matchedNames = ingredients
      .map(ingredient => ingredient.product || ingredient.name)
      .filter(Boolean)
      .join(', ');

    return { ingredientsWithContext: ingredientsText, substitutions: [], matchedNames };
  }

  const allMatchedItems = matchedProducts.flatMap(category => category.items || []);
  const ingredientLines = [];
  const substitutions = [];
  const matchedNames = [];

  for (const ingredient of ingredients) {
    const originalName = (ingredient.product || ingredient.name || '').trim();
    const ingredientName = originalName.toLowerCase();

    const matchedItem = allMatchedItems.find(item => {
      if (!item || !ingredientName) return false;

      const itemName = (item.name || '').toLowerCase();
      const originalItemName = (item.originalName || '').toLowerCase();
      const allOriginals = item.allOriginals || [];

      return itemName.includes(ingredientName)
        || ingredientName.includes(itemName)
        || originalItemName.includes(ingredientName)
        || ingredientName.includes(originalItemName)
        || allOriginals.some(original => {
          const normalizedOriginal = String(original).toLowerCase();
          return normalizedOriginal.includes(ingredientName) || ingredientName.includes(normalizedOriginal);
        });
    });

    if (matchedItem?.productName) {
      const matchedName = getLocalizedName(matchedItem) || matchedItem.productName;
      matchedNames.push(matchedItem.productName);

      const lowerMatched = matchedName.toLowerCase();
      const lowerOriginal = originalName.toLowerCase();
      const isSubstitution = lowerMatched !== lowerOriginal
        && !lowerMatched.includes(lowerOriginal)
        && !lowerOriginal.includes(lowerMatched);

      if (isSubstitution) {
        ingredientLines.push(`${ingredient.qty} ${matchedName} (originally: ${originalName})${getFormTag(ingredient)}`);
        substitutions.push({ original: originalName, actual: matchedName, quantity: ingredient.qty });
      } else {
        ingredientLines.push(`${ingredient.qty} ${matchedName}${getFormTag(ingredient)}`);
      }
    } else {
      ingredientLines.push(`${formatIngredientLine(ingredient)}${getFormTag(ingredient)}`);
      matchedNames.push(originalName);
    }
  }

  return {
    ingredientsWithContext: ingredientLines.join(', '),
    substitutions,
    matchedNames: matchedNames.filter(Boolean).join(', ')
  };
};


const buildSubstitutionNote = (substitutions) => {
  if (!substitutions.length) return '';

  const subList = substitutions
    .map(substitution => `"${substitution.original}" -> "${substitution.actual}"`)
    .join('; ');

  return `\nSubstitutions made by store matching: ${subList}. Adjust steps, quantities, cooking time, and title to the actual ingredients.`;
};

const buildShortNamesList = (recipe, substitutions) => normalizeIngredients(recipe)
  .map(ingredient => {
    const originalName = (ingredient.product || ingredient.name || '').trim();
    const substitution = substitutions.find(item => item.original.toLowerCase() === originalName.toLowerCase());
    return substitution ? substitution.actual : originalName;
  })
  .filter(Boolean)
  .join(', ');

const buildLanguageInstruction = (currentLang, isNonEnglish) => {
  if (!isNonEnglish) return '';

  const languageName = LANG_NAMES[currentLang];
  return `\nLANGUAGE: Write every user-facing text field in ${languageName}. Exceptions that MUST stay English: visual_description and ingredient_storage[].name. Do not copy raw English ingredient phrases into translated fields. Translate ingredient names naturally and localize cooking units in cooking_instructions: no tsp/tbsp/cup/oz/lb/ml/g in ${languageName} text when the locale has natural equivalents.`;
};

const buildServingsContext = (recipe) => {
  const numberOfPeople = recipe.numberOfPeople || 1;
  const cookFor2Days = recipe.cookFor2Days || false;
  const totalServings = numberOfPeople * (cookFor2Days ? 2 : 1);

  if (cookFor2Days) {
    return `Servings: ${numberOfPeople} ${numberOfPeople === 1 ? 'person' : 'people'} for 2 days (${totalServings} total servings). Quantities already include this; write instructions for the full batch.`;
  }

  if (numberOfPeople > 1) {
    return `Servings: ${numberOfPeople} people. Quantities already include this.`;
  }

  return '';
};

const buildTargetContext = (recipe, minStorageDays, cookingLimit) => {
  const capText = cookingLimit ? ` cooking_time MUST NOT exceed ${cookingLimit} min total (prep + cooking).` : '';
  const cookingTarget = recipe.cookingTime
    ? `Cooking time target: about ${recipe.cookingTime} min.${capText}`
    : (cookingLimit ? `Cooking time MUST NOT exceed ${cookingLimit} min total (prep + cooking).` : '');
  const storageTarget = `Required minimum storage: ${minStorageDays} days; this is meal ${minStorageDays === 3 ? '1' : minStorageDays === 4 ? '2' : '3'} within its meal type.`;
  return [cookingTarget, storageTarget].filter(Boolean).join(' ');
};

const normalizeFishFreshnessPrompt = (prompt) => prompt
  .replace(
    'For fresh fish/seafood this is 2 even when stored in freezer.',
    'Fresh fish/seafood in the fridge caps the dish at 2 days. If the target would be 3 days, keep it in the fridge and lower max_storage_time to 2. Use freezer only for fish/seafood targets beyond 3 days.'
  )
  .replace(
    'Fresh fish/seafood shelf_life_days MUST be 2; if this meal is intended beyond day 2, set fish/seafood storage to freezer but keep shelf_life_days=2. Freezing does not increase shelf_life_days.',
    'Fresh fish/seafood in fridge MUST have shelf_life_days=2. If this meal target is 3 days, prefer better taste: keep fish/seafood in fridge and lower max_storage_time to 2. Only if the meal must last beyond 3 days, set fish/seafood storage to freezer and set shelf_life_days to the effective planned freshness days. Freezer fish/seafood MUST have a thaw step before cooking.'
  )
  .replace(
    'fresh fish/seafood shelf_life_days is 2; freezer fish/seafood has a first thaw step;',
    'fridge fish/seafood shelf_life_days is 2; 3-day fish/seafood targets are lowered to max_storage_time 2 instead of freezing; freezer fish/seafood supports only targets beyond 3 days and has a first thaw step;'
  )
  .replace(
    'for freezer fish/seafood it MUST be the first cooking step.',
    'for freezer fish/seafood it MUST be the first cooking step and MUST name the actual ingredient, not generic fish.'
  )
  .replace(
    'freezer fish/seafood has a first thaw step;',
    'freezer fish/seafood has a first thaw step naming the actual ingredient;'
  );

const buildCookingInstructionSchemaBlock = ({ guide, complexity, currentLang, isNonEnglish }) => {
  const examples = STEP_TITLE_EXAMPLES[currentLang] || STEP_TITLE_EXAMPLES.en;
  const languageText = isNonEnglish ? ` in ${LANG_NAMES[currentLang]}` : '';

  return `cooking_instructions schema (CRITICAL):
Return grouped major cooking phases, not one action per step. Use this exact object shape:
"cooking_instructions": [
  {
    "step_title": "${examples.prep}",
    "details": [
      "Wash and dry the vegetables before cutting them into even pieces.",
      "Season the protein with salt and pepper while the oven or pan heats."
    ]
  },
  {
    "step_title": "${examples.cook}",
    "details": [
      "Arrange the main ingredients in one layer so they cook evenly.",
      "Cook until the center is done and the edges are lightly browned."
    ]
  },
  {
    "step_title": "${examples.finish}",
    "details": [
      "Let the hot components rest briefly so juices settle before serving.",
      "Finish with the listed garnish and serve while the texture is best."
    ]
  }
]
Step-title rules:
- Every step_title MUST be meaningful${languageText} and MUST include a time in parentheses, e.g. "${examples.prep}".
- NEVER use generic numbered headings: "Step 1", "Step 2", "Шаг 1", "Paso 1", "Etapa 1", "Passo 1", "Pas 1", "Крок 1".
- Use ${guide.steps} grouped phases for ${complexity} complexity. Do not split every action into its own step.
- Each non-thaw phase MUST have 2-4 detail sentences. A freezer thaw phase may have 1 detail, but its title still needs time, e.g. "${examples.thaw}".
- Freezer fish/seafood thaw instructions must be same-day: keep sealed, thaw in cold water for 20-30 min, then cook immediately. Do NOT tell the user to thaw overnight or move fish to the fridge the night before.
- The thaw step MUST name the actual frozen ingredient: shrimp/prawns/gambas/креветки for shrimp, mussels/clams for shellfish, salmon/cod/etc. for fish. Do NOT write generic "fish"/"рыбу" when the ingredient is shrimp or another seafood.
- If a recipe is very simple, keep fewer phases but still group related prep/cook/finish actions under the same title.`;
};

export const createRecipeDetailsPrompt = (recipe, matchedProducts, options = {}) => {
  const minStorageDays = options.minStorageDays || recipe?.minStorageDays || 3;
  const complexity = recipe?.complexity || 'standard';
  const activeCookingLimit = getCookingTimeLimit(recipe?.mealType, complexity);
  const cookingLimit = getEffectiveCookingTimeLimit(recipe?.mealType, complexity);
  const guide = COMPLEXITY_GUIDE[complexity] || COMPLEXITY_GUIDE.standard;
  const currentLang = getCurrentLanguage();
  const isNonEnglish = currentLang !== 'en' && LANG_NAMES[currentLang];
  const languageInstruction = buildLanguageInstruction(currentLang, isNonEnglish);
  const { ingredientsWithContext, substitutions, matchedNames } = buildIngredientContext(recipe, matchedProducts);
  const shortNamesList = buildShortNamesList(recipe, substitutions);
  const substitutionNote = buildSubstitutionNote(substitutions);
  const targetContext = `${buildTargetContext(recipe, minStorageDays, cookingLimit)} Seafood freshness rule: fresh fish and seafood kept in the fridge are 2-day ingredients. If this meal target would be 3 days, keep fish/seafood in the fridge and lower max_storage_time to 2 for better taste. Only if this meal must last beyond 3 days, store fish/seafood in freezer, set shelf_life_days to the effective planned freshness days, and start cooking_instructions with a same-day cold-water thaw step that names the actual frozen ingredient.`;
  const servingsContext = buildServingsContext(recipe);
  const instructionSchemaBlock = buildCookingInstructionSchemaBlock({ guide, complexity, currentLang, isNonEnglish });

  const prompt = `Recipe details JSON.\nTitle: ${recipe.title}.\nDescription: ${recipe.description || 'A delicious meal'}.\nAvailable ingredients: ${ingredientsWithContext}.${substitutionNote}\nShort names for cooking steps: ${shortNamesList}\n${targetContext}${servingsContext ? `\n${servingsContext}` : ''}\n\n${getCompactNamingRule()}${languageInstruction}\n\nReturn EXACTLY ONE valid JSON object, no markdown, comments, or extra keys. Keys MUST appear in this order: updated_title, visual_description, full_description, cooking_time, max_storage_time, ingredient_storage, cooking_instructions, tips, equipment.\n\nField rules:\n- updated_title: output language${isNonEnglish ? ` (${LANG_NAMES[currentLang]})` : ''}; max 40 characters; reflect actual ingredients/substitutions; no cuisine demonyms/adjectives (Spanish, Greek, Italian, French, Mediterranean, Asian, Mexican, Japanese, Chinese, Thai, Indian, American); no beverage pairings.\n- visual_description: English string only, max 500 chars, exact sections separated by \\n: STYLE, SHOT, LIGHTING, SUBJECT, PLATING, INGREDIENTS, GARNISH, BACKGROUND. STYLE starts with "appetizing food photography" and must convey a genuine homemade, cooked-at-home look — casual everyday meal photographed on a phone in a real kitchen, honest and unstyled, NOT a glossy advertisement, food-magazine spread, restaurant dish, studio promo, or fast-food commercial. LIGHTING: soft natural daylight from a window with gentle shadows; no harsh studio spotlight or artificial sheen. PLATING: a simple worn everyday plate or bowl with casual, slightly messy home plating; a modest realistic portion; little or no garnish, and no fussy chef-style arrangement or sauce swooshes. BACKGROUND: an ordinary lived-in home kitchen counter or plain wooden table, softly blurred and a little cluttered. SUBJECT must describe visible texture/color/state realistically, avoiding exaggerated gloss or hyper-saturated color. No CAMERA or COLOR fields.\n- full_description: output language; 1-2 simple sentences, max 260 chars; describe only the actual dish; no demonyms or beverage pairings.\n- cooking_time: string number only, realistic total minutes including prep + cooking${cookingLimit ? `; MUST be <= ${cookingLimit}` : ''}.\n- max_storage_time: string number only; finished dish stays genuinely fresh/tasty; use the minimum shelf_life_days across ingredients. Fresh fish/seafood in the fridge caps the dish at 2 days; if the target would be 3 days, keep it in the fridge and lower max_storage_time to 2.\n- ingredient_storage: one entry for EVERY available ingredient: {"name":"English ingredient name from input","storage":"fridge|freezer|pantry","shelf_life_days":number}. Prefer fridge/pantry. Fresh fish/seafood shelf_life_days MUST be 2 in the fridge. If the meal target is 3 days, keep fish/seafood in the fridge and lower max_storage_time to 2. Use freezer only when fish/seafood must last beyond 3 days or non-fish fridge life is below ${minStorageDays}.\n\n${getShelfLifeCatalog()}\n\nCooking instructions:\n- ${guide.steps} steps total for ${complexity} complexity. ${guide.technique}\n- Order prep -> cooking -> plating. Each detail sub-step: one imperative sentence, 10-20 words, max 2-4 details per step.\n- Realistic timing & passive cooking: step times MUST be physically honest. Raw potatoes, root vegetables, casseroles and bakes need 30-45 min in the oven (around 200C) — NEVER under-bake them to hit a short time. Oven/roast/braise/simmer time is mostly UNATTENDED: count it in cooking_time but keep active hands-on work light (around ${activeCookingLimit} min). Prefer ONE mostly-passive method over pre-frying or pre-boiling components and THEN baking.\n- Start every detail with an imperative verb. Use digits: 20 min, 180°C, 2 tbsp.\n- Use short kitchen names from the Short names list, translated when needed; never store product names.\n- First mention only: include each ingredient quantity once in the whole recipe.\n- Natural quantity placement: ingredient first then quantity, e.g. "butter (10g)"; liquids may use natural quantity-first grammar.\n- Use correct grammar/declension in non-English text; never capitalize ingredient names mid-sentence unless sentence start.\n- If ingredient_storage uses freezer, include a thaw/defrost instruction before cooking; for freezer fish/seafood it MUST be the first cooking step and MUST name the actual ingredient, not generic fish.\n- Supermarket-ready assumption: never tell the user to grind meat, mill flour, make butter/cheese/dough from scratch, or use industrial/special equipment.\n- Ingredient form: a [form] tag after an ingredient (e.g. "[canned]") is its real supermarket form. canned/jarred/crushed/prepared items are NOT fresh — never slice, peel, core, deseed, or pre-cook them; use them straight from the package (add, tip in, drain, stir through). Dried items usually need rehydrating or boiling first. Untagged items are fresh.\n- Recipe-ingredient consistency: if actual ingredients cannot make the original concept, adapt the method/title/description to the real dish.\n\nOther rules:\n- tips: exactly 1 short practical tip.\n- equipment: essential tools only. ${guide.equipment}\n- Seasoning (CRITICAL for taste — home cooks routinely under-season): season in LAYERS, never only once at the end. Salt and pepper proteins BEFORE cooking; salt the cooking water for potatoes, pasta, rice, grains, and boiled vegetables; season vegetables as they roast or saute; and finish with a "taste and adjust salt and pepper before serving" step. Use plain "salt and pepper" with no gram quantities — they are pantry staples and MUST NOT be added to the ingredient or shopping list.\n- Prefer oven roasting/baking for fish, chicken, and vegetables when equally suitable, unless the title implies pan-frying/stir-frying.\n\nCooking step examples (wrong -> right):\n- "Add 200g Organic Free-Range Eggs" -> "Crack eggs (2) into the hot pan"\n- "Season with Flaky Sea Salt (2g)" -> "Season with salt and pepper to taste"\n- "Slice the tomato into rounds" (canned tomato) -> "Tip in the chopped tomatoes with their juice"\n- "10g Unsalted Butter melt in pan" -> "Melt butter (10g) in the pan over medium heat"\n- "Take the Fresh Atlantic Salmon Fillet" -> "Place the salmon fillet skin-side down"\n- "Двести грамм муки просеять" -> "Просейте муку (200 г) в миску"\n- "Добавьте Оливковое Масло Extra Virgin (15 мл)" -> "Влейте оливковое масло (1 ст.л.)"\n\nSelf-check before output: all 9 keys in order; visual_description English and structured; all other user-facing fields localized; every ingredient represented in ingredient_storage; fresh fish/seafood shelf_life_days is 2; 3-day fish/seafood targets lower max_storage_time to 2 instead of freezing; freezer fish/seafood beyond 3 days has a first thaw step naming the actual ingredient; no store names in cooking_instructions; salt and pepper seasoned in layers (protein before cooking, salted cooking water, final taste-and-adjust); shelf_life_days respects catalog; no demonyms/beverage pairings;${cookingLimit ? ` cooking_time within the ${cookingLimit} min cap;` : ''} title matches actual ingredients. Output JSON only.`;
  const enhancedPrompt = prompt
    .replace('\n\nCooking instructions:\n-', `\n\n${instructionSchemaBlock}\n\nCooking instructions:\n-`)
    .replace(LEGACY_OVEN_PREFERENCE_RULE, LOW_STRESS_COOKING_RULE);

  return {
    prompt: normalizeFishFreshnessPrompt(enhancedPrompt),
    ingredientsWithContext,
    substitutions,
    ingredientNames: matchedNames,
    shortNamesList,
    minStorageDays
  };
};